import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of, BehaviorSubject, finalize, shareReplay, tap, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Borne haute pour `/api/category/`. Le chargement étant partagé, une requête
 * qui ne répond jamais bloquerait tous ses demandeurs — dont le resolver de
 * route, ce qui laisserait l'écran sur une page vide.
 */
const CATEGORIES_HTTP_TIMEOUT_MS = 20000;
import { Category } from '../model/category.model';
import { MetaCategory } from '../model/meta-category.model';

@Injectable({
  providedIn: 'root',
})
export class CategoryService {
  //list of available meta categories
  public metaCategories: MetaCategory[] = [];
  //current meta category (set to first value by default when loaded)
  public currentMeta: MetaCategory | undefined = undefined;
  // List of available categories, by meta category ID
  public categories: { [metaId: number]: Category[] } = {};
  private categoriesAreLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private categoriesAreLoaded: boolean = false;
  private categoriesAreLoading = false;
  /** Chargement en cours, partagé entre tous les appelants concurrents. */
  private inFlightLoad$: Observable<boolean> | null = null;
  /** Identifie le chargement courant, pour que seul lui puisse libérer son verrou. */
  private loadGeneration = 0;

  constructor(private http: HttpClient) { }

  clear() {
    this.metaCategories = [];
    this.categories = {};
    this.categoriesAreLoaded = false;
    this.categoriesAreLoading = false;
    this.inFlightLoad$ = null;
    this.loadGeneration++;
    this.categoriesAreLoaded$.next(false);
  }

  /**
   * Garantit que les catégories sont chargées, en ne lançant qu'une seule
   * requête même si plusieurs écrans la demandent en même temps.
   *
   * L'observable retourné émet une valeur puis complète, ou remonte l'erreur —
   * jamais rien d'autre. Auparavant, `categoriesAreLoading` était posé à `true`
   * de façon synchrone et n'était remis à `false` que dans le `map` de succès :
   * si le tout premier `/api/category/` échouait (401 après expiration du token,
   * coupure réseau, requête annulée par une navigation), le verrou restait posé
   * pour toute la vie du SPA et `categoriesAreLoaded$` n'émettait jamais `true`.
   * Tous les appels suivants recevaient alors un observable qui n'émettait ni ne
   * complétait jamais, ce qui bloquait définitivement l'initialisation de
   * l'éditeur Stripo (generator-edit-v2) sur un simple `await`.
   */
  private ensureLoaded(): Observable<boolean> {
    if (this.categoriesAreLoaded) {
      return of(true);
    }

    if (!this.inFlightLoad$) {
      const generation = ++this.loadGeneration;
      this.categoriesAreLoading = true;
      this.inFlightLoad$ = this.loadCategories().pipe(
        timeout(CATEGORIES_HTTP_TIMEOUT_MS),
        tap((success) => {
          if (!success) {
            throw new Error('Failed to load categories');
          }
          this.categoriesAreLoaded = true;
          this.categoriesAreLoaded$.next(true);
        }),
        // Verrou relâché quoi qu'il arrive : succès comme erreur. Le test de
        // génération évite qu'un chargement tardif n'efface le handle d'un
        // chargement plus récent (cas : clear() puis nouvelle demande).
        finalize(() => {
          if (this.loadGeneration !== generation) return;
          this.categoriesAreLoading = false;
          this.inFlightLoad$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.inFlightLoad$;
  }

  /** Extrait les catégories déjà en cache pour les métas demandées. */
  private selectCategories(metaIds?: number[]): Category[] {
    if (metaIds) {
      return metaIds.reduce(
        (acc, metaId) => acc.concat(this.categories[metaId] || []),
        [] as Category[],
      );
    }
    if (this.currentMeta?.id) {
      return this.categories[this.currentMeta.id] || [];
    }
    return [];
  }

  // Loading categories, idealy should be done once
  loadCategories(): Observable<boolean> {
    return this.http
      .get<{ meta_category: { [key: string]: string };[key: string]: { [key: string]: string } }>(
        `${environment.backendUrl}/api/category/`,
      )
      .pipe(
        map((result) => {
          const metaCategoriesData = result.meta_category;
          this.metaCategories = [];

          // Loading meta categories
          for (const [key, value] of Object.entries(metaCategoriesData)) {
            this.metaCategories.push(new MetaCategory(parseInt(key), value));
          }

          // Setting default current meta
          if (!this.currentMeta) {
            this.currentMeta = this.metaCategories[0];
          }

          // Building categories sets
          this.metaCategories.forEach((meta) => {
            const metaId = meta.id;
            if (result.hasOwnProperty(metaId + '') && typeof result[metaId] === 'object') {
              const categories: Category[] = [];
              for (const [key, value] of Object.entries(result[metaId])) {
                categories.push(new Category(parseInt(key), value, metaId)); // Include meta_category here
              }
              this.categories[metaId] = categories;
            }
          });

          return true;
        }),
      );
  }

  /**
   * Sets current meta category by its ID
   * @param metaId
   */
  setCurrentMeta(metaId: number | string) {
    this.currentMeta = this.metaCategories.find((mc) => mc.id === Number(metaId));
  }

  // Return available categories for current meta, loads categories & meta if needed.
  getCurrentMeta(): Observable<MetaCategory> {
    if (this.categoriesAreLoaded && this.currentMeta) {
      return of(this.currentMeta);
    }

    return this.ensureLoaded().pipe(
      map(() => {
        if (!this.currentMeta) {
          throw new Error('No current meta category');
        }
        return this.currentMeta;
      }),
    );
  }

  getCategoryById(id: number, metaId: number): Category | undefined {
    const categories = this.categories[metaId];
    if (categories) {
      return categories.find((cat) => cat.id === id);
    }
    return undefined;
  }

  // Retourne les catégories disponibles pour la méta donnée, charge les catégories et la méta si nécessaire.
  getCategories(metaIds?: number[]): Observable<Category[]> {
    if (this.categoriesAreLoaded) {
      return of(this.selectCategories(metaIds));
    }

    return this.ensureLoaded().pipe(map(() => this.selectCategories(metaIds)));
  }

  getPlanningCategories(): Observable<{ metas: MetaCategory[], cats: { [metaId: number]: Category[] } }> {
    return this.http
      .get<{ meta_category: { [key: string]: string };[key: string]: { [key: string]: string } }>(
        `${environment.backendUrl}/api/category/?planning=true`,
      )
      .pipe(
        map((result) => {
          const metas: MetaCategory[] = [];
          const cats: { [metaId: number]: Category[] } = {};

          const metaCategoriesData = result.meta_category;
          for (const [key, value] of Object.entries(metaCategoriesData)) {
            metas.push(new MetaCategory(parseInt(key), value));
          }

          metas.forEach((meta) => {
            const metaId = meta.id;
            if (result.hasOwnProperty(metaId + '') && typeof result[metaId] === 'object') {
              const categories: Category[] = [];
              for (const [key, value] of Object.entries(result[metaId])) {
                categories.push(new Category(parseInt(key), value, metaId));
              }
              cats[metaId] = categories;
            }
          });

          return { metas, cats };
        })
      );
  }
}
